import React from "react";
import { Link } from "react-router-dom";

const milestones = [
  {
    year: "2012",
    title: "The Dream Begins",
    text: "Ali Hassan, a Skardu-born mountaineering guide, returns from K2 base camp with a vision: a lodge where adventurers feel truly at home.",
    accent: "bg-ocean-500",
  },
  {
    year: "2015",
    title: "Doors Open",
    text: "Mariner Lodge opens with just three rooms, hand-built using local timber and stone. The panoramic terrace becomes an instant legend among trekkers.",
    accent: "bg-teal-500",
  },
  {
    year: "2019",
    title: "Expanding the Vision",
    text: "The Indus River Suite is added, along with a solar power system. Mariner Lodge earns its first TripAdvisor Certificate of Excellence.",
    accent: "bg-brand",
  },
  {
    year: "2024",
    title: "A Landmark Destination",
    text: "Ranked #1 guest house in Skardu. Welcoming guests from 40+ countries, still family-run, still committed to sustainable mountain hospitality.",
    accent: "bg-sand-500",
  },
];

const values = [
  {
    title: "Community First",
    desc: "We hire locally, source ingredients from village farms, and invest 10% of profits in Skardu community projects.",
    iconBg: "bg-ocean-100 text-ocean-700",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    title: "Sustainable by Design",
    desc: "Solar panels, rainwater harvesting, composting, zero single-use plastic — we protect the pristine wilderness that draws you here.",
    iconBg: "bg-teal-100 text-teal-700",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: "Authentic Experiences",
    desc: "Every meal tells a story. Every trail recommendation comes from a guide who has walked it a hundred times. No scripted tourism here.",
    iconBg: "bg-sand-100 text-sand-700",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
];

export default function AboutStory() {
  return (
    <>
      {/* ── Story section ─────────────────────────── */}
      <section className="section-pad bg-white">
        <div className="container-base">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            {/* Text */}
            <div>
              <p className="section-subtitle mb-3">Our Story</p>
              <h2 className="section-title mb-6">
                Born from the Mountains,<br />
                <span className="text-gradient-ocean">Built for Adventurers</span>
              </h2>
              <div className="space-y-4 text-slate-500 text-body-md">
                <p>
                  Mariner Lodge was born from a simple belief: that the best
                  moments in life happen when breathtaking landscapes meet
                  genuine human warmth. Our founder, Ali Hassan, spent years
                  guiding climbers to K2 base camp before deciding to create a
                  place where those climbers could rest, recover, and truly
                  connect with the Karakoram.
                </p>
                <p>
                  We built the lodge stone by stone, timber by timber, with
                  help from the local community. Every room, every meal, and
                  every recommendation you receive carries the soul of Skardu.
                </p>
                <p>
                  Today, guests from over 40 countries have sat on our
                  panoramic terrace, watched the sun paint K2 gold, and left
                  changed. That never gets old.
                </p>
              </div>
              <div className="flex gap-4 mt-8">
                <Link to="/booking" className="btn-primary">
                  Stay With Us
                </Link>
                <a href="/#contact" className="btn-ghost">
                  Get In Touch
                </a>
              </div>
            </div>

            {/* Image collage */}
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80"
                alt="K2 peak at sunrise"
                loading="lazy"
                className="rounded-2xl object-cover w-full h-52 shadow-ocean"
              />
              <img
                src="https://images.unsplash.com/photo-1502126324834-38f8e02d7160?w=600&q=80"
                alt="Skardu valley from above"
                loading="lazy"
                className="rounded-2xl object-cover w-full h-52 mt-8 shadow-teal"
              />
              <img
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80"
                alt="Warm lodge interior"
                loading="lazy"
                className="rounded-2xl object-cover w-full h-52 shadow-card"
              />
              <img
                src="https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=600&q=80"
                alt="Trekking trails near Skardu"
                loading="lazy"
                className="rounded-2xl object-cover w-full h-52 mt-8 shadow-sand"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Timeline section ─────────────────────── */}
      <section className="section-pad bg-section-light">
        <div className="container-base">
          <div className="text-center mb-14">
            <p className="section-subtitle mb-3">Our Journey</p>
            <h2 className="section-title">Milestones</h2>
          </div>
          <div className="relative max-w-3xl mx-auto">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-ocean-100 hidden sm:block" aria-hidden="true" />
            <ol className="space-y-10">
              {milestones.map((m, i) => (
                <li key={i} className="relative flex gap-6 items-start">
                  {/* Dot */}
                  <div
                    className={`w-16 h-16 rounded-full ${m.accent} text-white flex items-center justify-center font-serif font-bold text-lg flex-shrink-0 shadow-card z-10`}
                    aria-hidden="true"
                  >
                    {m.year}
                  </div>
                  <div className="pt-3">
                    <h3 className="font-serif font-bold text-xl text-slate-900 mb-1">
                      {m.title}
                    </h3>
                    <p className="text-slate-500 text-body-sm leading-relaxed">{m.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── Values section ───────────────────────── */}
      <section className="section-pad bg-white">
        <div className="container-base">
          <div className="text-center mb-14">
            <p className="section-subtitle mb-3">What We Stand For</p>
            <h2 className="section-title">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <div key={i} className="card-ocean p-8 rounded-2xl border border-ocean-100">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${v.iconBg}`}>
                  {v.icon}
                </div>
                <h3 className="font-serif font-bold text-xl text-slate-900 mb-2">
                  {v.title}
                </h3>
                <p className="text-slate-500 text-body-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
